// Product Registry -- auto-discovers product configs from per-product config.ts files.
//
// Each product can optionally provide a config.ts with navigation, accent colors,
// icons, and landing page. Products without a config get sensible defaults.
import type { ComponentType } from 'react';
import {
  HomeIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

export interface NavItem {
  name: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  /** When true, this nav item is only rendered for users with role === 'superadmin'. */
  requiresSuperadmin?: boolean;
}

export interface ProductConfig {
  slug: string;
  navigation: NavItem[];
  accentColor: { bg: string; border: string; text: string };
  icon?: ComponentType<{ className?: string }>;
  landingPage: string;
}

// Default accent (cyan) -- used by most products
const defaultAccent = {
  bg: 'bg-cyan-500/10',
  border: 'border-cyan-500/30',
  text: 'text-cyan-400',
};

// Default navigation for products without a custom config
function defaultNavigation(slug: string): NavItem[] {
  return [
    { name: 'DASHBOARD', href: `/${slug}/dashboard`, icon: HomeIcon },
    { name: 'SETTINGS', href: `/${slug}/settings`, icon: Cog6ToothIcon },
  ];
}

// Route aliases: URL path segment → registry slug
const ROUTE_ALIASES: Record<string, string> = {
  'data-vault': 'datavault',
};

/**
 * Resolve a URL path segment to its registry slug.
 */
export function resolveProductSlug(routeSegment: string): string {
  return ROUTE_ALIASES[routeSegment] || routeSegment;
}

// Registry storage
const registry = new Map<string, ProductConfig>();

// Auto-discover all config.ts files in product subdirectories
const modules = import.meta.glob<{ default: ProductConfig }>('./*/config.ts', { eager: true });
for (const [path, mod] of Object.entries(modules)) {
  const config = mod.default;
  if (config?.slug) {
    registry.set(config.slug, config);
  }
}

/**
 * Get a product config by slug. Returns a default config if no custom config exists.
 */
export function getProductConfig(slug: string): ProductConfig {
  const resolved = resolveProductSlug(slug);
  const custom = registry.get(resolved);
  if (custom) return custom;

  // Return sensible defaults for unregistered products
  return {
    slug,
    navigation: defaultNavigation(slug),
    accentColor: defaultAccent,
    landingPage: `/${slug}/dashboard`,
  };
}

/**
 * Get all registered product configs.
 */
export function getAllProductConfigs(): ProductConfig[] {
  return Array.from(registry.values());
}
